import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import Typography from '@mui/material/Typography';
import IconLargeCicle from '../../shared/IconLargeCicle';
import CircularProgress from '@mui/material/CircularProgress';
import {
  useCancelScheduleByIdWithTokenMutation,
  useVerifyActiveScheduleByIdMutation,
  VerifyActiveScheduleDTO
} from '../../../services/schedule.service';
import { formatDateLabel } from '../../../lib/dayjs';
import ExpiredConfirmationSchedule from '../ExpiredConfirmationSchedule';
import Button from '@mui/material/Button';
import { useIsLoggedIn } from '../../../shared/hooks';
import { setRedirect } from '../../../features/redirect';
import { useDispatch } from 'react-redux';
import { dynamicCustomEventTracking } from '../../../lib/gtm';
import { ButtonWrapper } from './styled';

export default function CancelSignalScheduleRoot() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isLogged = useIsLoggedIn();
  const [isLoading, setIdLoading] = useState(true);
  const [isExpire, seIsExpire] = useState(true);
  const { scheduleId } = useParams();
  const [verifyActiveScheduleData, setVerifyActiveScheduleData] = useState<VerifyActiveScheduleDTO>();
  const [verifyActiveScheduleById] = useVerifyActiveScheduleByIdMutation();
  const [cancelScheduleByIdWithToken, { isLoading: isLoadingCancelJourney }] = useCancelScheduleByIdWithTokenMutation();

  const handleReschedule = () => {
    if (isLogged) {
      dynamicCustomEventTracking.send({
        dynamicEventName: 'signal_reschedule_logged_mc'
      });
      navigate(`/jornada/${verifyActiveScheduleData?.journey.id}`);
    } else {
      dynamicCustomEventTracking.send({
        dynamicEventName: 'signal_reschedule_logged_mc'
      });
      dispatch(setRedirect(`/jornada/${verifyActiveScheduleData?.journey.id}`));
      navigate(`/login`);
    }
  };

  const handleCancelSchedule = () => {
    cancelScheduleByIdWithToken({ id: verifyActiveScheduleData?.id || '', token: verifyActiveScheduleData?.token || '' })
      .unwrap()
      .then(res => {
        if (!res) {
          seIsExpire(true);
          return;
        }
        dynamicCustomEventTracking.send({
          dynamicEventName: 'signal_cancel_schedule_mc'
        });
        navigate(`/motivo-do-cancelamento/${verifyActiveScheduleData?.id}`, {
          state: { token: verifyActiveScheduleData?.token }
        });
      })
      .catch(() => {
        seIsExpire(true);
      });
  };

  useEffect(() => {
    verifyActiveScheduleById({ id: scheduleId || '' })
      .unwrap()
      .then(res => {
        if (!res) {
          seIsExpire(true);
          setIdLoading(false);

          return;
        }
        setIdLoading(false);
        seIsExpire(false);
        setVerifyActiveScheduleData(res);
      })
      .catch(() => {
        seIsExpire(true);
        setIdLoading(false);
      });
  }, []);

  if (isLoading || isLoadingCancelJourney) {
    return <CircularProgress sx={{ color: 'brand.darkGreen' }} />;
  }

  if (isExpire) {
    return <ExpiredConfirmationSchedule />;
  }

  return (
    <>
      <IconLargeCicle>
        <ErrorOutlineOutlinedIcon />
      </IconLargeCicle>

      <Typography variant="h2" sx={{ mb: 3 }}>
        Deseja cancelar ou reagendar sua consulta?
      </Typography>
      <Typography variant="body1" sx={{ mb: 2 }}>
        A Dra {verifyActiveScheduleData?.user.name} está com a agenda reservada para você dia{' '}
        {formatDateLabel(verifyActiveScheduleData?.dateStart || '')}. Pedimos que cancele ou reagende o quanto antes para que
        esta data e horário sejam liberados para outras Oyanas.
      </Typography>

      <ButtonWrapper>
        <Button variant="contained" color="secondary" onClick={() => handleCancelSchedule()}>
          Cancelar consulta
        </Button>
        <Button variant="contained" color="primary" onClick={() => handleReschedule()}>
          Reagendar
        </Button>
      </ButtonWrapper>
    </>
  );
}
